import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Warning, ArrowRight } from '@phosphor-icons/react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Filter from '../../Filter/Filter'
import CheckboxField from '../../FormLegacy/FormFieldsV2/CheckboxField'
import { useSnackbar } from '../../../helpers/hooks/useSnackbar'
import {
  fetchNotRepliedCount,
  fetchRecentReviews,
  getRecentReviews,
  getNotRepliedCount,
  getRefetchRecentReviews,
  setRefetchRecentReviews,
} from '../../../reducers/reputationManagementReducer'

import Box from './Box'
import Review from './Review'
import NoReviewsFound from './NoReviewsFound'
import { getRatingOptions, getPlatformOptions, getPeriodTime } from './helpers'

const MAX_REVIEWS = 10

const RecentReviews = ({ goToReviews, period, buildingId }) => {
  const { t } = useTranslation(['organisation'])
  const ratingOptions = getRatingOptions(t)
  const platformOptions = getPlatformOptions(t)
  const [notReplied, setNotReplied] = useState(false)
  const [rating, setRating] = useState(ratingOptions[0])
  const [platform, setPlatform] = useState(platformOptions[0])
  const [isLoading, setIsLoading] = useState(false)
  const { displaySnackbar } = useSnackbar()
  const notRepliedCount = useSelector(getNotRepliedCount)
  const recentReviews = useSelector(getRecentReviews)
  const refetchRecentReviews = useSelector(getRefetchRecentReviews)

  const dispatch = useDispatch()

  const fetchNotRepliedCountAction = (...args) => dispatch(fetchNotRepliedCount(...args))
  const fetchRecentReviewsAction = (...args) => dispatch(fetchRecentReviews(...args))
  const setRefetchRecentReviewsAction = (...args) => dispatch(setRefetchRecentReviews(...args))

  const getNotRepliedCountAction = useCallback(async () => {
    try {
      const { from, to } = getPeriodTime(period)
      await fetchNotRepliedCountAction(buildingId, { from, to })
    } catch (error) {
      console.error(error)
    }
  }, [buildingId, period])

  const fetchReviews = useCallback(async () => {
    try {
      setIsLoading(true)
      const { from, to } = getPeriodTime(period)
      const filter = {
        from,
        to,
        notReplied,
        skip: 0,
        limit: MAX_REVIEWS,
      }

      if (platform.value !== 'all') {
        filter.provider = platform.value
      }

      if (rating.value !== 0) {
        filter.rating = rating.value
      }

      await fetchRecentReviewsAction(buildingId, filter)
      getNotRepliedCountAction()
    } catch (error) {
      displaySnackbar(t('organisation:reputationManagement.errors.fetchReviewsFailed'), true)
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }, [buildingId, notReplied, rating, platform, period])

  useEffect(() => {
    if (buildingId) {
      fetchReviews()
    }
  }, [buildingId, fetchReviews])

  useEffect(() => {
    if (refetchRecentReviews) {
      fetchReviews()
      setRefetchRecentReviewsAction(false)
    }
  }, [refetchRecentReviews, fetchReviews])

  return (
    <Box
      title={t('organisation:reputationManagement.recentReviews.title')}
      label={
        notRepliedCount > 0 && (
          <div className="reputation-dashboard-tab__reviews--label">
            {t('organisation:reputationManagement.recentReviews.toReplyLabel', {
              count: notRepliedCount,
            })}
            <Warning size={16} />
          </div>
        )
      }
      headerActions={
        <div className="reputation-dashboard-tab__reviews--actions">
          <CheckboxField
            onChange={() => setNotReplied((prev) => !prev)}
            value={notReplied}
            name="notReplied"
            label={t('organisation:reputationManagement.filters.havenTRepliedLabel')}
          />
          <Filter
            placeholder={rating.text}
            options={ratingOptions}
            value={[rating]}
            name="rating"
            label={t('organisation:reputationManagement.filters.ratingLabel')}
            additionalClass={'reputation-dashboard-tab__reviews--filter'}
            onChange={(e) => setRating(e.target.value)}
          />
          <Filter
            placeholder={platform.text}
            options={platformOptions}
            value={[platform]}
            name="platform"
            label={t('organisation:reputationManagement.recentReviews.platformLabel')}
            additionalClass={'reputation-dashboard-tab__reviews--filter'}
            onChange={(e) => setPlatform(e.target.value)}
          />
        </div>
      }
      content={
        <div className={`reputation-dashboard-tab__reviews ${isLoading ? 'is-loading' : ''}`}>
          {recentReviews.length > 0 ? (
            <React.Fragment>
              {recentReviews.map((review) => (
                <Review isRecent key={review.id} review={review} refetchReviews={fetchReviews} />
              ))}
              <div className="reputation-dashboard-tab__go-to-btn" onClick={goToReviews}>
                {t('organisation:reputationManagement.recentReviews.goToAllReviews')}
                <ArrowRight size={20} />
              </div>
            </React.Fragment>
          ) : (
            <NoReviewsFound />
          )}
        </div>
      }
    />
  )
}

RecentReviews.propTypes = {
  goToReviews: PropTypes.func,
  buildingId: PropTypes.number,
  period: PropTypes.object,
}

export default RecentReviews
